import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { formatDate } from "@/lib/utils"
import { Head, usePage } from "@inertiajs/react"
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconExternalLink,
  IconMail,
  IconPencil,
  IconPhone,
} from "@tabler/icons-react"
import {
  STATUS_BADGE as DOCUMENT_STATUS_BADGE,
  EXPIRY_CLASS,
  type ExpiryLevel,
  expiryLabel,
} from "../LegalDocuments/status"

interface ProjectSummary {
  id: number
  name: string
  description: string | null
  project_type: string
  status: string
  start_date: string | null
  end_date: string | null
}

interface LegalDocumentSummary {
  id: number
  title: string
  document_type: string
  document_type_label: string
  status: string
  effective_date: string | null
  end_date: string | null
  expiry_level: ExpiryLevel
  days_until_expiry: number | null
  document_url: string | null
}

interface Customer {
  id: number
  name: string
  legal_name: string | null
  alias: string | null
  tax_no: string | null
  contact_name: string | null
  contact_email: string
  contact_phone: string | null
  address: string | null
  website: string | null
  active: boolean
  color: string | null
  avatar_url: string | null
  logo_url: string | null
  projects: ProjectSummary[]
  legal_documents: LegalDocumentSummary[]
  can_update: boolean
  can_create_project: boolean
  can_read_documents: boolean
  can_create_document: boolean
}

interface Props {
  [key: string]: unknown
  customer: Customer
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  active: { variant: "default", label: "Active" },
  in_progress: { variant: "default", label: "In Progress" },
  planning: { variant: "secondary", label: "Planning" },
  completed: { variant: "outline", label: "Completed" },
  on_hold: { variant: "destructive", label: "On Hold" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

export default function CustomerShow() {
  const { customer } = usePage<Props>().props

  return (
    <>
      <Head title={`${customer.name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/customers">Customers</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{customer.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/customers">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div className="flex items-center gap-3">
                {customer.color && <div className="h-3 w-3 rounded-full" style={{ backgroundColor: customer.color }} />}
                <div>
                  <div className="flex items-center gap-2.5">
                    <h1 className="text-lg font-semibold tracking-tight">{customer.name}</h1>
                    <Badge variant={customer.active ? "default" : "destructive"}>
                      {customer.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  {customer.legal_name && customer.legal_name !== customer.name && (
                    <p className="text-muted-foreground text-sm">{customer.legal_name}</p>
                  )}
                </div>
              </div>
            </div>

            {customer.can_update && (
              <Button size="sm" asChild>
                <a href={`/customers/${customer.id}/edit`}>
                  <IconPencil className="mr-1.5 h-4 w-4" />
                  Edit
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Main - Projects */}
          <div className="space-y-6 xl:col-span-2">
            <div className="rounded-xl border bg-card">
              <div className="flex items-center justify-between p-5 pb-0">
                <h3 className="text-sm font-medium">Projects ({customer.projects.length})</h3>
                {customer.can_create_project && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={`/customers/${customer.id}/projects/new`}>New Project</a>
                  </Button>
                )}
              </div>
              {customer.projects.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Dates</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customer.projects.map((project) => {
                        const badge = STATUS_BADGE[project.status] || {
                          variant: "outline" as const,
                          label: project.status,
                        }
                        return (
                          <TableRow key={project.id}>
                            <TableCell>
                              <a href={`/projects/${project.id}`} className="text-primary hover:underline font-medium">
                                {project.name}
                              </a>
                            </TableCell>
                            <TableCell className="text-muted-foreground capitalize">
                              {project.project_type.replace("_", " ")}
                            </TableCell>
                            <TableCell>
                              <Badge variant={badge.variant}>{badge.label}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm tabular-nums">
                              {formatDate(project.start_date)}
                              {project.end_date && ` \u2013 ${formatDate(project.end_date)}`}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-5">
                  <p className="text-muted-foreground text-center text-sm py-8">No projects yet</p>
                </div>
              )}
            </div>

            {/* Documents. Legal documents are admin-only, so the card is absent for
                everyone else rather than showing an empty shell. */}
            {customer.can_read_documents && (
              <div className="rounded-xl border bg-card">
                <div className="flex items-center justify-between p-5 pb-0">
                  <h3 className="text-sm font-medium">Documents ({customer.legal_documents.length})</h3>
                  {customer.can_create_document && (
                    <Button size="sm" variant="outline" asChild>
                      <a href={`/legal_documents/new?customer_id=${customer.id}`}>New Document</a>
                    </Button>
                  )}
                </div>
                {customer.legal_documents.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Dates</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customer.legal_documents.map((document) => {
                          const badge = DOCUMENT_STATUS_BADGE[document.status] || {
                            variant: "outline" as const,
                            label: document.status,
                          }
                          return (
                            <TableRow key={document.id}>
                              <TableCell>
                                <a
                                  href={`/legal_documents/${document.id}`}
                                  className="text-primary hover:underline font-medium"
                                >
                                  {document.title}
                                </a>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{document.document_type_label}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge variant={badge.variant}>{badge.label}</Badge>
                              </TableCell>
                              <TableCell className="text-sm tabular-nums">
                                <span className="text-muted-foreground">
                                  {formatDate(document.effective_date)}
                                  {document.end_date && ` – ${formatDate(document.end_date)}`}
                                </span>
                                <DocumentExpiry document={document} />
                              </TableCell>
                              <TableCell className="text-right">
                                {document.document_url && (
                                  <a
                                    href={document.document_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Open ${document.title}`}
                                    className="text-muted-foreground hover:text-foreground inline-flex"
                                  >
                                    <IconExternalLink className="h-4 w-4" />
                                  </a>
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="p-5">
                    <p className="text-muted-foreground text-center text-sm py-8">No documents yet</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Contact</h3>
              <dl className="space-y-2.5 text-sm">
                {customer.contact_name && <MetaRow label="Name" value={customer.contact_name} />}
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd>
                    <a
                      href={`mailto:${customer.contact_email}`}
                      className="text-primary hover:underline inline-flex items-center gap-1"
                    >
                      {customer.contact_email}
                      <IconMail className="h-3 w-3" />
                    </a>
                  </dd>
                </div>
                {customer.contact_phone && (
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Phone</dt>
                    <dd className="font-medium inline-flex items-center gap-1">
                      <IconPhone className="h-3 w-3 text-muted-foreground" />
                      {customer.contact_phone}
                    </dd>
                  </div>
                )}
                {customer.website && (
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Website</dt>
                    <dd>
                      <a
                        href={customer.website.startsWith("http") ? customer.website : `https://${customer.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                      >
                        {customer.website.replace(/^https?:\/\//, "")}
                        <IconExternalLink className="h-3 w-3" />
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Details */}
            <div className="rounded-xl border bg-card p-5">
              <h3 className="mb-3 text-sm font-medium">Details</h3>
              <dl className="space-y-2.5 text-sm">
                {customer.alias && <MetaRow label="Alias" value={customer.alias} />}
                {customer.tax_no && <MetaRow label="Tax No." value={customer.tax_no} />}
                {customer.address && (
                  <>
                    <Separator />
                    <div>
                      <dt className="text-muted-foreground mb-1">Address</dt>
                      <dd className="font-medium whitespace-pre-line">{customer.address}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function DocumentExpiry({ document }: { document: LegalDocumentSummary }) {
  const label = expiryLabel(document.expiry_level, document.days_until_expiry)
  if (!label) return null

  return (
    <span
      className={`ml-1.5 inline-flex items-center gap-1 text-xs font-medium ${EXPIRY_CLASS[document.expiry_level || ""]}`}
    >
      <IconAlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {label}
    </span>
  )
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}

CustomerShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
